import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Box,
  Typography,
  Card,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  Popover,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useQuery } from "react-query";
import { ApiEndPoints, IMAGE_URL, MEDIA_URL } from "src/network/endpoints";
import InfoIcon from "src/@core/components/icons/InfoIcon";
import { axiosInstance } from "src/network/adapter";
import { toast } from "react-hot-toast";
import currencyData from "src/constants/currencyData.json";

// Styled Components - Following the same design patterns
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000,
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "900px",
  height: "700px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "80vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 25,
  height: 25,
  cursor: "se-resize",
  background:
    "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)",
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 15,
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`,
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  cursor: "move",
  userSelect: "none",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: 8,
  transform: "translateX(-50%)",
  width: 40,
  height: 4,
  backgroundColor: "#E2E8F0",
  borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ScrollableContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
});

const CartItemCard = styled(Card)(({ theme }) => ({
  marginBottom: 12,
  borderRadius: 12,
  boxShadow: "0 2px 12px -2px rgba(0,0,0,0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "1px solid #F1F5F9",
  overflow: "hidden",
  "&:hover": {
    boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
    transform: "translateY(-1px)",
    borderColor: "#E2E8F0",
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: 6,
    borderRadius: 8,
  },
}));

const CartItemContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 12,
  padding: 12,
  [theme.breakpoints.down("sm")]: { gap: 6, padding: 6 },
}));

const ItemImageContainer = styled(Box)(({ theme }) => ({
  width: 60,
  height: 50,
  borderRadius: 8,
  overflow: "hidden",
  flexShrink: 0,
  backgroundColor: "#F8FAFC",
  position: "relative",
  boxShadow: "0 2px 8px -1px rgba(0,0,0,0.08)",
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
    borderRadius: 6,
  },
}));

const QuantityControls = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 3,
  borderRadius: 6,
  padding: "1px",
  backgroundColor: "#F8FAFC",
  border: "1px solid #E2E8F0",
  [theme.breakpoints.down("sm")]: {
    gap: 2,
    padding: "1px",
    borderRadius: 4,
  },
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  minWidth: 20,
  height: 20,
  borderRadius: "50%",
  backgroundColor: "#6200EE",
  padding: "0px",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#5A00D6",
    transform: "scale(1.05)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    minWidth: 16,
    height: 16,
  },
}));

const BottomActions = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 5,
  paddingTop: "8px",
  borderTop: "2px solid #E2E8F0",
  marginTop: "auto",
  [theme.breakpoints.down("sm")]: {
    gap: 12,
    paddingTop: "6px",
  },
  [theme.breakpoints.down(400)]: {
    gap: 8,
    paddingTop: "4px",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  height: 56,
  borderRadius: 16,
  textTransform: "lowercase",
  fontSize: "1rem",
  fontWeight: 700,
  "&.primary": {
    background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
    color: "white",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 35px -8px rgba(98, 0, 238, 0.6)",
    },
  },
  "&.secondary": {
    background: "#F2E9FF",
    color: "#6200EE",
    border: "2px solid #E2E8F0",
    "&:hover": {
      backgroundColor: "#6200EE",
      color: "white",
      transform: "translateY(-2px)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    height: 40,
    borderRadius: 8,
    fontSize: "0.8rem",
  },
  [theme.breakpoints.down(400)]: {
    height: 36,
    borderRadius: 6,
    fontSize: "0.75rem",
  },
}));

const CartSummary = styled(Box)(({ theme }) => ({
  backgroundColor: "#F8FAFC",
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  border: "1px solid #E2E8F0",
  [theme.breakpoints.down("sm")]: {
    padding: 8,
    marginBottom: 8,
    borderRadius: 8,
  },
}));

// Constants
const commonTransition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
const primaryColor = "#6200EE";
const primaryLight = "#F2E9FF";

const CartDialog = ({
  open,
  onClose,
  cartData,
  onPlaceOrder,
  onBack,
  onItemClick, // Add new prop for handling item clicks
  loading = false,
  conversationType = "main",
  conversationId, // Add conversationId prop to use when placing orders
  businessLocationId
}) => {
  const theme = useTheme();
  const dialogRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [expandedVariants, setExpandedVariants] = useState({});
  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);
  const [selectedIngredientModifications, setSelectedIngredientModifications] =
    useState([]);
  const [orderMode, setOrderMode] = useState("seat_down"); // Default to seat_down
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Get location ID from cart data
  const locationId = businessLocationId;
  //  console.log("locationId------>", locationId);

  // Fetch business data using React Query
  // const { data: businessData, isLoading: businessLoading, error: businessError } = useQuery(
  //   ["businessLocationProfile", locationId],
  //   async ({ signal }) => {
  //     if (!locationId) return null;
      
  //     const res = await axiosInstance.get(
  //       ApiEndPoints.BUSINESS_LOCATION.getAll + "/" + locationId,
  //       { signal }
  //     );
  //     return res.data.data;
  //   },
  //   {
  //     refetchOnWindowFocus: false,
  //     cacheTime: 30 * 60 * 1000, // Cache for 30 minutes
  //     enabled: !!locationId && open, // Only fetch when location ID exists and dialog is open
  //   }
  // );

  
  // Drag and resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY)),
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;

        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Center dialog when opened
  useEffect(() => {
    if (open) {
      const isMobile = window.innerWidth < 960;
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 900;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 700;
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;

      setDialogPosition({
        x: Math.max(0, centerX),
        y: Math.max(0, centerY),
      });
    }
  }, [open]);

  // Event handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  // Utility functions
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return "$";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : "$";
  };

  const formatPrice = (price, currencyCode = "USD") => {
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${price}`;
  };
  
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return imagePath.startsWith("http")
      ? imagePath
      : `${IMAGE_URL}${imagePath}`;
  };

  // Helper function to get currency from cart item
  const getCartItemCurrency = (cartItem) => {
    if (!cartItem || !cartItem.menuItemId) return "USD";
    
    const { menuItemId, variantEnabled, variants, locationId } = cartItem;
    
    // Extract locationId string (handle both object and string)
    const locationIdStr = locationId?._id || locationId;
    
    if (variantEnabled && variants && variants.length > 0) {
      // For variant items, get currency from the first variant's perLocationStock
      const firstVariant = variants[0];
      if (firstVariant.items && firstVariant.items.length > 0) {
        // Check if variant items have currency
        const itemCurrency = firstVariant.items[0].currency;
        if (itemCurrency) return itemCurrency;
      }
      
      // Fallback: get from menuItemId variants perLocationStock
      if (menuItemId.variants && menuItemId.variants.length > 0) {
        const variant = menuItemId.variants.find(
          (v) => v._id === firstVariant.items?.[0]?.variantId
        );
        if (variant?.perLocationStock && variant.perLocationStock.length > 0) {
          // Try to match locationId first, then use first available
          const locationStock = locationIdStr
            ? variant.perLocationStock.find(
                (stock) => stock.locationId === locationIdStr || stock.locationId?._id === locationIdStr
              )
            : variant.perLocationStock[0];
          if (locationStock?.currency) return locationStock.currency;
        }
      }
    } else {
      // For non-variant items, get currency from locationStock
      if (menuItemId.locationStock && menuItemId.locationStock.length > 0) {
        // Try to match locationId first, then use first available
        const locationStock = locationIdStr
          ? menuItemId.locationStock.find(
              (stock) => stock.locationId === locationIdStr || stock.locationId?._id === locationIdStr
            )
          : menuItemId.locationStock[0];
        if (locationStock?.currency) return locationStock.currency;
      }
    }
    
    // Fallback to USD if no currency found
    return "USD";
  };

  // Toggle variant expansion
  const toggleVariant = (cartItemId, variantName) => {
    setExpandedVariants((prev) => ({
      ...prev,
      [`${cartItemId}-${variantName}`]: !prev[`${cartItemId}-${variantName}`],
    }));
  };

  // Info popover handlers
  const handleInfoPopoverOpen = (event, ingredientModifications) => {
    setInfoPopoverAnchor(event.currentTarget);
    setSelectedIngredientModifications(ingredientModifications);
  };

  const handleInfoPopoverClose = () => {
    setInfoPopoverAnchor(null);
    setSelectedIngredientModifications([]);
  };

  // Add handler for quantity button clicks that opens ItemOrderDetailDialog
  const handleQuantityClick = (cartItem, action) => {
    if (onItemClick) {
      // Pass the full menu item data as itemData and cart item as cartItemData
      onItemClick(cartItem.menuItemId, cartItem);
      // Close the cart dialog
      onClose();
    }
  };

  // Handle order placement
  const handlePlaceOrder = async () => {
    if (!cartData?.data?.cart?.cart || cartData.data.cart.cart.length === 0) {
      toast.error("cart is empty");
      return;
    }

    setIsPlacingOrder(true);
    try {
      // Get the first cart item to extract locationId
      const firstCartItem = cartData.data.cart.cart[0];
      if (!firstCartItem) {
        toast.error("invalid cart data");
        return;
      }

      // Extract locationId - handle both object and string formats
      // const locationId = firstCartItem.locationId?._id || firstCartItem.locationId;
      if (!locationId) {
        toast.error("location id not found");
        return;
      }

      // Use conversationId from prop if provided, otherwise fallback to cart data
      // This ensures we use the correct conversation ID (sub-conversation if in sub-conversation)
      const conversationIdToUse = conversationId || cartData.data.cart.conversationId;
      if (!conversationIdToUse) {
        toast.error("conversation id not found");
        return;
      }

 
      const orderPayload = {
        conversationId: conversationIdToUse,
        locationId: locationId,
        orderMode: orderMode,
        // taxes: taxes,
        // paymentSettings: paymentSettings,
        userConversationType: conversationType
      };

      const response = await axiosInstance.post(ApiEndPoints.ORDER.place, orderPayload);
      
      // Show the message from API response
      if (response.data.message) {
        toast.success(response.data.message);
      } else {
        toast.success("order placed  !");
      }

      // Call the parent's onPlaceOrder callback if provided
      if (onPlaceOrder) {
        onPlaceOrder(response.data);
      }
      // Close the cart dialog
      onClose();
    } catch (error) {
      console.error("error placing order:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "failed to place order";
      toast.error(errorMessage);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // Render cart item
  const renderCartItem = (cartItem, index) => {
    if (!cartItem || !cartItem.menuItemId) return null;
    
    const {
      menuItemId,
      quantity,
      totalPrice,
      note,
      variantEnabled,
      variants,
      items,
    } = cartItem;
    const imageUrl = getImageUrl(menuItemId.imageVideo?.[0]);
    const cartItemId = cartItem._id || index;
    const itemCurrency = getCartItemCurrency(cartItem);

    // Calculate total quantity and price for variant items
    let displayQuantity = quantity;
    let displayTotalPrice = totalPrice;

    if (variantEnabled && variants) {
      // Calculate total quantity from all variants
      displayQuantity = variants.reduce((total, variant) => {
        return (
          total +
          variant.items.reduce((variantTotal, item) => {
            return variantTotal + item.quantity;
          }, 0)
        );
      }, 0);

      // Calculate total price from all variants
      displayTotalPrice = variants.reduce((total, variant) => {
        return (
          total +
          variant.items.reduce((variantTotal, item) => {
            return variantTotal + item.totalPrice;
          }, 0)
        );
      }, 0);
    }

    // Check if item has customizations
    const hasCustomizations = variantEnabled
      ? variants?.some((variant) =>
          variant.items?.some(
            (item) =>
              item.customizations?.ingredientModifications?.some(
                (mod) => mod.action !== "no_modification"
              ) || item.customizations?.sides?.length > 0
          )
        )
      : items?.some(
          (item) =>
            item.customizations?.ingredientModifications?.some(
              (mod) => mod.action !== "no_modification"
            ) || item.customizations?.sides?.length > 0
        );

    return (
      <CartItemCard key={cartItemId}>
        <CartItemContent>
          {/* Item Details Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Item Header with Customized tag */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.8rem", sm: "1.2rem" },
                  color: "#1E293B",
                  textTransform: "lowercase",
                }}
              >
                {menuItemId.name}
              </Typography>
              {hasCustomizations && (
                <Chip
                  label="customized"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.6rem", sm: "0.7rem" },
                    fontWeight: 600,
                    backgroundColor: primaryColor,
                    color: "white",
                    textTransform: "lowercase",
                  }}
                />
              )}
            </Box>

            {/* Item Description */}
            <Typography
              variant="body2"
              sx={{
                color: "#64748B",
                fontSize: { xs: "0.65rem", sm: "1rem" },
                textTransform: "lowercase",
              }}
            >
              {menuItemId.description}
            </Typography>

            {/* Item Code */}
            <Typography
              variant="body2"
              sx={{
                color: "#64748B",
                fontSize: { xs: "0.6rem", sm: "0.9rem" },
                mb: { xs: 0.5, sm: 0.75 },
                textTransform: "lowercase",
                fontWeight: 500,
              }}
            >
              {menuItemId.code || `#${menuItemId._id?.slice(-4)}`}
            </Typography>

            {/* Item Note - Only show for non-variant items */}
            {!variantEnabled && note && note.trim() && (
              <Typography
                variant="body2"
                sx={{
                  color: "#6C00E6",
                  fontSize: { xs: "0.6rem", sm: "0.75rem" },
                  mb: { xs: 0.75, sm: 1 },
                  textTransform: "lowercase",
                  fontWeight: 500,
                  fontStyle: "italic",
                  backgroundColor: "#F2E9FF",
                  padding: { xs: "3px 6px", sm: "4px 8px" },
                  borderRadius: 0.5,
                  border: "1px solid #E2E8F0",
                }}
              >
                note: {note.trim()}
              </Typography>
            )}
          </Box>

          {/* Right side - Image and Quantity Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Item Image */}
            <ItemImageContainer>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={menuItemId.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "1.5rem", sm: "1.8rem" },
                  }}
                >
                  🍽️
                </Box>
              )}
            </ItemImageContainer>

            {/* Quantity Controls */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <QuantityControls>
                <QuantityButton
                  size="small"
                  onClick={() => handleQuantityClick(cartItem, "decrease")}
                >
                  <RemoveIcon
                    sx={{
                      color: "#fff",
                      width: { xs: 14, sm: 16 },
                      height: { xs: 14, sm: 16 },
                    }}
                  />
                </QuantityButton>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    color: primaryColor,
                    minWidth: { xs: 14, sm: 18 },
                    textAlign: "center",
                    cursor: "pointer", // Make quantity clickable
                  }}
                  onClick={() => handleQuantityClick(cartItem, "edit")}
                >
                  {displayQuantity}
                </Typography>
                <QuantityButton
                  size="small"
                  onClick={() => handleQuantityClick(cartItem, "increase")}
                >
                  <AddIcon
                    sx={{
                      color: "#fff",
                      width: { xs: 14, sm: 16 },
                      height: { xs: 14, sm: 16 },
                    }}
                  />
                </QuantityButton>
              </QuantityControls>

              {/* Item Total */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  color: primaryColor,
                  textAlign: "center",
                }}
              >
                total : {formatPrice(displayTotalPrice, itemCurrency)}
              </Typography>
            </Box>
          </Box>
        </CartItemContent>

        {/* Variants or Items Section */}
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Variants or Items */}
          {variantEnabled && variants ? (
            <Box sx={{ mb: { xs: 1, sm: 2 } }}>
              {variants.map((variant, variantIndex) => {
                const variantKey = `${cartItemId}-${variant.variantName}`;
                const isExpanded = expandedVariants[variantKey];
                const totalVariantQuantity = variant.items.reduce(
                  (sum, item) => sum + item.quantity,
                  0
                );

                return (
                  <Box key={variantIndex} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: isExpanded ? "#F8FAFC" : "transparent",
                        "&:hover": { backgroundColor: "#F8FAFC" },
                      }}
                      onClick={() =>
                        toggleVariant(cartItemId, variant.variantName)
                      }
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1E293B",
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          textTransform: "lowercase",
                        }}
                      >
                        {totalVariantQuantity}x {variant.variantName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#64748B",
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        ▼
                      </Typography>
                    </Box>

                    {isExpanded && (
                      <Box sx={{ ml: 2, mb: 1 }}>
                        {/* Variant Items */}
                        {variant.items.map((item, itemIndex) => (
                          <Box
                            key={itemIndex}
                            sx={{
                              mb: 1,
                              p: 1,
                              backgroundColor: "#F8FAFC",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#000",
                                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                                textTransform: "lowercase",
                                mb: 1,
                              }}
                            >
                              {item.quantity}x -{" "}
                              {formatPrice(item.variantPrice, item.currency || itemCurrency)} each
                            </Typography>

                            {/* Customizations Summary */}
                            {item.customizations?.ingredientModifications && (
                              <Box sx={{ mb: 1 }}>
                                {(() => {
                                  const modifications =
                                    item.customizations.ingredientModifications;
                                  const removeCount = modifications.filter(
                                    (mod) => mod.action === "remove"
                                  ).length;
                                  const subCount = modifications.filter(
                                    (mod) => mod.action === "substitute"
                                  ).length;
                                  const noChangeCount = modifications.filter(
                                    (mod) => mod.action === "no_modification"
                                  ).length;

                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        alignItems: "center",
                                      }}
                                    >
                                      {removeCount > 0 && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#000",
                                            fontSize: {
                                              xs: "0.7rem",
                                              sm: "0.75rem",
                                            },
                                            textTransform: "lowercase",
                                          }}
                                        >
                                          {removeCount}x remove
                                        </Typography>
                                      )}
                                      {subCount > 0 && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#000",
                                            fontSize: {
                                              xs: "0.7rem",
                                              sm: "0.75rem",
                                            },
                                            textTransform: "lowercase",
                                          }}
                                        >
                                          {subCount}x sub
                                        </Typography>
                                      )}
                                      {noChangeCount > 0 && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#000",
                                            fontSize: {
                                              xs: "0.7rem",
                                              sm: "0.75rem",
                                            },
                                            textTransform: "lowercase",
                                          }}
                                        >
                                          {noChangeCount}x no change
                                        </Typography>
                                      )}
                                      {modifications.length > 0 && (
                                        <Box
                                          onMouseEnter={(e) =>
                                            handleInfoPopoverOpen(
                                              e,
                                              modifications
                                            )
                                          }
                                          sx={{
                                            cursor: "pointer",
                                            transition: "color 0.2s ease",
                                            display: "flex",
                                            alignItems: "start",
                                            bgcolor: "#E1E0DA",
                                            p: 0.5,
                                            borderRadius: 1,
                                            ml: 1,
                                            "&:hover": {
                                              "& svg path": {
                                                fill: primaryColor,
                                              },
                                            },
                                          }}
                                        >
                                          <InfoIcon
                                            size={
                                              window.innerWidth < 600
                                                ? 10
                                                : window.innerWidth < 960
                                                ? 10
                                                : 12
                                            }
                                            color="#64748B"
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                            )}

                            {/* Sides */}
                            {item.customizations?.sides?.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                {item.customizations.sides.map(
                                  (side, sideIndex) => (
                                    <Typography
                                      key={sideIndex}
                                      variant="body2"
                                      sx={{
                                        color: "#000",
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.75rem",
                                        },
                                        textTransform: "lowercase",
                                      }}
                                    >
                                      +{side.quantity} x add {side.sideName}{" "}
                                      {formatPrice(side.price, side.currency || itemCurrency)}
                                    </Typography>
                                  )
                                )}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Variant Note - Show outside expanded area */}
                    {variant.note && variant.note.trim() && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6C00E6",
                          fontSize: { xs: "0.6rem", sm: "0.7rem" },
                          mt: 1,
                          ml: 2,
                          textTransform: "lowercase",
                          fontWeight: 500,
                          fontStyle: "italic",
                          backgroundColor: "#F2E9FF",
                          padding: { xs: "2px 4px", sm: "3px 6px" },
                          borderRadius: 0.5,
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        note: {variant.note.trim()}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : items && items.length > 0 ? (
            <Box sx={{ mb: { xs: 1, sm: 2 } }}>
              {items.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    mb: 1,
                    p: 1,
                    backgroundColor: "#F8FAFC",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1E293B",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      textTransform: "lowercase",
                      mb: 1,
                    }}
                  >
                    item {item.itemNumber}
                  </Typography>

                  {/* Customizations Summary */}
                  {item.customizations?.ingredientModifications && (
                    <Box sx={{ mb: 1 }}>
                      {(() => {
                        const modifications =
                          item.customizations.ingredientModifications;
                        const removeCount = modifications.filter(
                          (mod) => mod.action === "remove"
                        ).length;
                        const subCount = modifications.filter(
                          (mod) => mod.action === "substitute"
                        ).length;
                        const noChangeCount = modifications.filter(
                          (mod) => mod.action === "no_modification"
                        ).length;

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {removeCount > 0 && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#000",
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  textTransform: "lowercase",
                                }}
                              >
                                {removeCount}x remove
                              </Typography>
                            )}
                            {subCount > 0 && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#000",
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  textTransform: "lowercase",
                                }}
                              >
                                {subCount}x sub
                              </Typography>
                            )}
                            {noChangeCount > 0 && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#000",
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  textTransform: "lowercase",
                                }}
                              >
                                {noChangeCount}x no change
                              </Typography>
                            )}
                            {modifications.length > 0 && (
                              <Box
                                onMouseEnter={(e) =>
                                  handleInfoPopoverOpen(e, modifications)
                                }
                                sx={{
                                  cursor: "pointer",
                                  transition: "color 0.2s ease",
                                  display: "flex",
                                  alignItems: "start",
                                  bgcolor: "#E1E0DA",
                                  p: 0.5,
                                  borderRadius: 1,
                                  ml: 1,
                                  "&:hover": {
                                    "& svg path": {
                                      fill: primaryColor,
                                    },
                                  },
                                }}
                              >
                                <InfoIcon
                                  size={
                                    window.innerWidth < 600
                                      ? 10
                                      : window.innerWidth < 960
                                      ? 10
                                      : 12
                                  }
                                  color="#64748B"
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Sides */}
                  {item.customizations?.sides?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {item.customizations.sides.map((side, sideIndex) => (
                        <Typography
                          key={sideIndex}
                          variant="body2"
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          +{side.quantity}x {side.sideName}{" "}
                          {formatPrice(side.price, side.currency || itemCurrency)}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      </CartItemCard>
    );
  };

  if (!open) return null;

  const cartItems = cartData?.data?.cart?.cart || [];
  const summary = cartData?.data?.summary || {
    totalItems: 0,
    totalQuantity: 0,
    totalAmount: 0,
  };

  return (
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Back Button */}
            {onBack && (
              <IconButton
                onClick={onBack}
                size={theme.breakpoints.down("sm") ? "small" : "medium"}
                sx={{
                  color: primaryColor,
                  backgroundColor: primaryLight,
                  "&:hover": {
                    backgroundColor: primaryColor,
                    color: "white",
                    transform: "scale(1.05)",
                  },
                  transition: commonTransition,
                }}
              >
                <ArrowBackIcon
                  fontSize={theme.breakpoints.down("sm") ? "small" : "medium"}
                />
              </IconButton>
            )}
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "0.9rem", sm: "1.2rem", md: "1.25rem" },
                textTransform: "lowercase",
                color: "#000",
              }}
            >
              cart items
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size={theme.breakpoints.down("sm") ? "small" : "medium"}
            sx={{
              color: primaryColor,
              backgroundColor: primaryLight,
              "&:hover": {
                backgroundColor: primaryColor,
                color: "white",
                transform: "scale(1.05)",
              },
              transition: commonTransition,
            }}
          >
            <CloseIcon
              fontSize={theme.breakpoints.down("sm") ? "small" : "medium"}
            />
          </IconButton>
        </DialogHeader>

        <ContentContainer>
          <ScrollableContainer>
            {/* Header Section - Business Info */}
            {cartItems.length > 0 && cartItems[0]?.businessId && (
              <Box
                sx={{
                  mb: { xs: 2, sm: 3 },
                  p: 2,
                  backgroundColor: "#F8FAFC",
                  borderRadius: 2,
                  border: "1px solid #E2E8F0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  {/* Business Location Avatar */}
                  <Box
                    sx={{
                      width: { xs: 50, sm: 80, md: 100 },
                      height: { xs: 50, sm: 80, md: 100 },
                      borderRadius: 2,
                      overflow: "hidden",
                      flexShrink: 0,
                      backgroundColor: "#E2E8F0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {cartItems[0].locationId?.locationProfilePhoto ? (
                      <img
                        src={`${MEDIA_URL}${cartItems[0].locationId.locationProfilePhoto}`}
                        alt={cartItems[0].locationId.businessLocationName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "1.2rem", sm: "1.5rem" },
                          color: "#64748B",
                        }}
                      >
                        🏪
                      </Box>
                    )}
                  </Box>

                  {/* Business Info */}
                  <Box sx={{ flex: 1 }}>
                    {/* Business Name */}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "0.9rem", sm: "1.4rem" },
                        color: "#1E293B",
                        textTransform: "lowercase",
                        mb: 1,
                      }}
                    >
                      {cartItems[0].businessId.businessName}
                    </Typography>

                    {/* Info Rows: Left vs Right */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      {/* Left Column */}
                      <Box>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.7rem", sm: "0.9rem" },
                            textTransform: "lowercase",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: { xs: 100, sm: 250 },
                          }}
                        >
                          {cartItems[0].locationId?.physicalAddress ||
                            "address not available"}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#6C00E6",
                            fontSize: { xs: "0.7rem", sm: "0.9rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          tax invoice
                        </Typography>

                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.7rem", sm: "0.9rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          {cartItems[0].locationId?.emailAddress ||
                            "email not available"}
                        </Typography>
                      </Box>

                      {/* Right Column */}
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          vat#:{" "}
                          {cartItems[0].locationId?.businessVatNumber || "n/a"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          reg#:{" "}
                          {cartItems[0].locationId
                            ?.businessRegistrationNumber || "n/a"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#000",
                            fontSize: { xs: "0.75rem", sm: "0.8rem" },
                            textTransform: "lowercase",
                          }}
                        >
                          tel:{" "}
                          {cartItems[0].locationId?.phoneNumber?.number ||
                            "n/a"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Order Details Section */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.9rem", sm: "1.1rem" },
                color: "#1E293B",
                textTransform: "lowercase",
                mb: { xs: 1, sm: 2 },
              }}
            >
              order details
            </Typography>

            {/* Cart Items */}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: { xs: 6, sm: 8 },
                  color: "#64748B",
                }}
              >
                <CircularProgress
                  size={60}
                  sx={{
                    color: primaryColor,
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                    textTransform: "lowercase",
                    fontWeight: 600,
                  }}
                >
                  loading cart items...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    textTransform: "lowercase",
                    mt: 1,
                    opacity: 0.7,
                  }}
                >
                  please wait while we fetch your items
                </Typography>
              </Box>
            ) : cartItems.length > 0 ? (
              cartItems.map(renderCartItem)
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: { xs: 6, sm: 8 },
                  color: "#64748B",
                }}
              >
                <CartIcon
                  sx={{
                    fontSize: { xs: "3rem", sm: "4rem" },
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                    textTransform: "lowercase",
                  }}
                >
                  your cart is empty
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    textTransform: "lowercase",
                    mt: 1,
                    opacity: 0.7,
                  }}
                >
                  add some delicious items to get started
                </Typography>
              </Box>
            )}

            {/* Order Summary */}
            {!loading && cartItems.length > 0 && (
              <>
                <Divider sx={{ my: 2, borderColor: "#E2E8F0" }} />
                <CartSummary>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#000",
                        fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        textTransform: "lowercase",
                      }}
                    >
                      number of items
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#000",
                        fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        textTransform: "lowercase",
                      }}
                    >
                      {summary.totalItems}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#000",
                        fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        textTransform: "lowercase",
                      }}
                    >
                      total quantity
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#000",
                        fontSize: { xs: "0.7rem", sm: "0.9rem" },
                        textTransform: "lowercase",
                      }}
                    >
                      {summary.totalQuantity}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "0.9rem", sm: "1.2rem" },
                        color: "#1E293B",
                        textTransform: "lowercase",
                      }}
                    >
                      grand total:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "0.9rem", sm: "1.2rem" },
                        color: primaryColor,
                      }}
                    >
                      {formatPrice(summary.totalAmount, cartItems.length > 0 ? getCartItemCurrency(cartItems[0]) : "USD")}
                    </Typography>
                  </Box>
                </CartSummary>
              </>
            )}
          </ScrollableContainer>

          {/* Bottom Actions */}
          {!loading && cartItems.length > 0 && (
            <BottomActions>
              {/* Delivery/Pickup Options */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 1,
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "#F8FAFC" },
                  }}
                  onClick={() => setOrderMode("seat_down")}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: "2px solid #6200EE",
                      backgroundColor:
                        orderMode === "seat_down" ? "#6200EE" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {orderMode === "seat_down" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: orderMode === "seat_down" ? "#6200EE" : "#000",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      textTransform: "lowercase",
                      fontWeight: orderMode === "seat_down" ? 600 : 500,
                    }}
                  >
                    sit down
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "#F8FAFC" },
                  }}
                  onClick={() => setOrderMode("collect")}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: "2px solid #6200EE",
                      backgroundColor:
                        orderMode === "collect" ? "#6200EE" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {orderMode === "collect" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: orderMode === "collect" ? "#6200EE" : "#000",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      textTransform: "lowercase",
                      fontWeight: orderMode === "collect" ? 600 : 500,
                    }}
                  >
                    collect
                  </Typography>
                </Box>
              </Box>

              {/* Place Order Button */}
              <ActionButton
                className="primary"
                onClick={handlePlaceOrder}
                disabled={isPlacingOrder}
                sx={{ width: "100%" }}
              >
                {isPlacingOrder ? (
                  <>
                    <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                    placing order...
                  </>
                ) : (
                  `place order (${summary.totalQuantity})`
                )}
              </ActionButton>
            </BottomActions>
          )}
        </ContentContainer>

        <ResizeHandle onMouseDown={handleResizeStart} />
      </CustomDialog>

      {/* Info Popover */}
      <Popover
        open={Boolean(infoPopoverAnchor)}
        anchorEl={infoPopoverAnchor}
        onClose={handleInfoPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
        sx={{
          zIndex: 10001, // Higher than dialog z-index (10000)
          "& .MuiPopover-paper": {
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            border: "1px solid #E2E8F0",
            padding: { xs: 1, sm: 1.5, md: 1.5 },
            minWidth: { xs: 200, sm: 220, md: 240 },
            maxWidth: { xs: "80vw", sm: 300, md: 320 },
            mt: 0.5,
            zIndex: 10001,
            // Adjust positioning for xs screens
            "&": {
              [theme.breakpoints.down("sm")]: {
                left: "50% !important",
                transform: "translateX(-50%) !important",
                right: "auto !important",
              },
            },
          },
        }}
      >
        {/* Ingredient Modifications Section */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.8rem",
              color: "#1E293B",
              mb: 1,
              textTransform: "lowercase",
            }}
          >
            ingredient modifications
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {selectedIngredientModifications.map((modification, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  p: 0.75,
                  backgroundColor: "#F8FAFC",
                  borderRadius: 0.5,
                  border: "1px solid #E2E8F0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1E293B",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "lowercase",
                    lineHeight: 1.2,
                  }}
                >
                  {modification.ingredientName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748B",
                    fontSize: "0.7rem",
                    textTransform: "lowercase",
                    lineHeight: 1.2,
                  }}
                >
                  {modification.action === "remove" && "remove ingredient"}
                  {modification.action === "substitute" &&
                    `substitute with ${modification.substituteWith}`}
                  {modification.action === "no_modification" && "no changes"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </CustomOverlay>
  );
};

export default CartDialog;
